import React, { useEffect, useState, useCallback } from 'react';
import { getCSVJobStatus, getBatchResults } from '../services/api';
import { CSVJobStatus, ProcessedRow, BatchResultRow } from '../types';
import './ProcessingStatus.css';

interface ProcessingStatusProps {
  jobId: string;
  onComplete: (rows: ProcessedRow[]) => void;
  onError: (error: string) => void;
}

const ProcessingStatus: React.FC<ProcessingStatusProps> = ({
  jobId,
  onComplete,
  onError,
}) => {
  const [status, setStatus] = useState<CSVJobStatus | null>(null);
  const [pollCount, setPollCount] = useState(0);

  // Convert API response to frontend ProcessedRow format
  const convertToProcessedRows = useCallback((results: BatchResultRow[]): ProcessedRow[] => {
    return results.map((result, index) => ({
      id: `row-${index + 1}`,
      query: result.query,
      response: result.response,
      evidence: result.evidence.join('; '),
      confidence: result.confidence,
      sources: result.sources,
      customFields: Object.fromEntries(
        Object.entries(result.custom_fields).map(([k, v]) => [k, String(v)])
      ),
      originalData: result.original_data,
      isEdited: false,
      debugInfo: result.debug_info,
    }));
  }, []);

  useEffect(() => {
    let interval: NodeJS.Timeout;

    const pollStatus = async () => {
      try {
        const jobStatus = await getCSVJobStatus(jobId);
        setStatus(jobStatus);
        setPollCount(c => c + 1);

        if (jobStatus.status === 'completed') {
          // Fetch JSON results instead of CSV
          const batchResults = await getBatchResults(jobId);
          const rows = convertToProcessedRows(batchResults.results);
          onComplete(rows);
        } else if (jobStatus.status === 'failed') {
          onError(jobStatus.error || 'Processing failed');
        }
      } catch (err: any) {
        onError(err.message || 'Failed to get job status');
      }
    };

    pollStatus();
    interval = setInterval(pollStatus, 2000);

    return () => clearInterval(interval);
  }, [jobId, onComplete, onError, convertToProcessedRows]);

  const progressPercent = status?.total ? Math.round((status.progress / status.total) * 100) : 0;

  return (
    <div className="processing-status">
      <h2>⏳ Processing Questionnaire</h2>
      
      <div className="status-card">
        <div className="status-header">
          <span className={`status-badge ${status?.status || 'pending'}`}>
            {status?.status?.toUpperCase() || 'STARTING'}
          </span>
          <span className="poll-info">Poll #{pollCount}</span>
        </div>

        <div className="progress-section">
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${progressPercent}%` }} />
          </div>
          <div className="progress-text">
            {status?.progress || 0} / {status?.total || '?'} questions ({progressPercent}%)
          </div>
        </div>

        {status?.current_query && (
          <div className="current-query">
            <span className="label">Currently processing:</span>
            <span className="query-text">{status.current_query}</span>
          </div>
        )}

        <div className="processing-animation">
          <div className="spinner" />
          <span>AI is analyzing your compliance questions...</span>
        </div>
      </div>
    </div>
  );
};

export default ProcessingStatus;

